import React, { useContext } from 'react';
import { Button } from 'react-bootstrap';
import { Building2, Hospital, ListFilter, CheckCircle } from 'lucide-react';
import GroupDetailsStep from '../components/GroupIntake/GroupDetailsStep';
import FacilityDetailsStep from '../components/GroupIntake/FacilityDetailsStep';
import ListingDetailsStep from '../components/GroupIntake/ListingDetailsStep';
import ListingPreview from '../components/GroupIntake/ListingPreview';
import ReviewStep from '../components/GroupIntake/ReviewStep';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { GroupIntakeWithQueryProvider, GroupIntakeContext } from '../components/GroupIntake/GroupIntakeContext';
import { useListing } from '../components/GroupIntake/contexts/ListingContext';
import './GroupIntakePage.css';

// Wrapper component that uses the provider
export default function GroupIntakePage() {
  return (
    <GroupIntakeWithQueryProvider>
      <GroupIntakeContent />
    </GroupIntakeWithQueryProvider>
  );
}

// Main content component that consumes the context
function GroupIntakeContent() {
  const { 
    currentStep, setCurrentStep,
    group, updateGroup,
    facilities, updateFacility, addFacility, removeFacility,
    updateListingField, updateClinicalField, addListing,
    errors, isSubmitting, submitSuccess, isLoading,
    handleSubmit,
    previewFacilityIndex, previewListingIndex, setPreviewFacilityIndex, setPreviewListingIndex,
    showDelFacModal, setShowDelFacModal, facToRemove,
    openDeleteFacilityModal, handleConfirmDeleteFacility, closeDeleteFacilityModal,
    showDelListModal, setShowDelListModal, listToRemove, setListToRemove,
  } = useContext(GroupIntakeContext);
  const { removeListing: deleteListingContext } = useListing();
  
  // Preview navigation
  const navigatePreview = (direction) => {
    const facility = facilities[previewFacilityIndex];
    if (!facility || !facility.listings || facility.listings.length === 0) return;
    
    const currentListings = facility.listings;
    
    if (direction === 'next') {
      if (previewListingIndex < currentListings.length - 1) {
        // Move to next listing in current facility
        setPreviewListingIndex(previewListingIndex + 1);
      } else if (previewFacilityIndex < facilities.length - 1) {
        // Move to first listing of next facility
        const nextFacility = facilities[previewFacilityIndex + 1];
        if (nextFacility.listings && nextFacility.listings.length > 0) {
          setPreviewFacilityIndex(previewFacilityIndex + 1);
          setPreviewListingIndex(0);
        }
      }
    } else if (direction === 'prev') {
      if (previewListingIndex > 0) {
        // Move to previous listing in current facility
        setPreviewListingIndex(previewListingIndex - 1);
      } else if (previewFacilityIndex > 0) {
        // Move to last listing of previous facility
        const prevFacility = facilities[previewFacilityIndex - 1];
        if (prevFacility.listings && prevFacility.listings.length > 0) {
          setPreviewFacilityIndex(previewFacilityIndex - 1);
          setPreviewListingIndex(prevFacility.listings.length - 1);
        }
      }
    }
  };

  // Get total listings count across all facilities
  const getTotalListingsCount = () => {
    return facilities.reduce((count, facility) => {
      return count + (facility.listings?.length || 0);
    }, 0);
  };

  // Get current overall listing index (across all facilities)
  const getCurrentOverallListingIndex = () => {
    let index = 0;
    for (let i = 0; i < previewFacilityIndex; i++) {
      index += facilities[i]?.listings?.length || 0;
    }
    return index + previewListingIndex;
  };

  // Define steps
  const steps = [
    { 
      id: 'group', 
      name: 'Group Details', 
      icon: <Building2 size={18} />,
    },
    { 
      id: 'facilities', 
      name: 'Facilities', 
      icon: <Hospital size={18} />,
    },
    { 
      id: 'listings', 
      name: 'Listings', 
      icon: <ListFilter size={18} />,
    },
    { 
      id: 'review', 
      name: 'Review', 
      icon: <CheckCircle size={18} />,
    },
  ];

  // Confirm removal functions
  const confirmRemoveFacility = (facilityId, idx) => {
    // Use the properly exposed openDeleteFacilityModal function
    if (typeof openDeleteFacilityModal === 'function') {
      openDeleteFacilityModal(facilityId, idx);
    } else {
      // Fallback if the function isn't available for some reason
      if (window.confirm(`Are you sure you want to delete this facility? All associated listings will also be deleted.`)) {
        removeFacility(idx);
      }
    }
  };

  const confirmRemoveListing = (facIdx, listIdx) => {
    setListToRemove({ facIdx, listIdx });
    setShowDelListModal(true);
  };

  // Go to step function - this is critical for navigation to work properly
  // Use setCurrentStep directly from context, don't wrap it in a local function
  const goToStep = setCurrentStep;

  // Render the current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <GroupDetailsStep 
            group={group} 
            updateGroup={updateGroup} 
            errors={errors}
          />
        );
      case 1:
        return (
          <FacilityDetailsStep 
            facilities={facilities}
            updateFacility={updateFacility}
            addFacility={addFacility}
            removeFacility={confirmRemoveFacility}
            errors={errors}
            group={group}
          />
        );
      case 2:
        return (
          <div className="split-view">
            <div className="left-column">
              <ListingDetailsStep 
                facilities={facilities}
                updateListingField={updateListingField}
                updateClinicalField={updateClinicalField}
                addListing={addListing}
                removeListing={confirmRemoveListing}
                errors={errors}
                previewFacilityIndex={previewFacilityIndex}
                previewListingIndex={previewListingIndex}
                setPreviewFacilityIndex={setPreviewFacilityIndex}
                setPreviewListingIndex={setPreviewListingIndex}
              />
            </div>
            <div className="right-column preview-column">
              <ListingPreview 
                group={group}
                facilities={facilities}
                currentFacilityIndex={previewFacilityIndex}
                currentListingIndex={previewListingIndex}
              />

              {getTotalListingsCount() > 1 && (
                <div className="preview-navigation">
                  <Button 
                    variant="outline-primary" 
                    className="prev-btn" 
                    onClick={() => navigatePreview('prev')}
                    disabled={previewFacilityIndex === 0 && previewListingIndex === 0}
                  >
                    Previous Listing
                  </Button>
                  <div className="preview-pagination">
                    {getCurrentOverallListingIndex() + 1} of {getTotalListingsCount()}
                  </div>
                  <Button 
                    variant="outline-primary" 
                    className="next-btn" 
                    onClick={() => navigatePreview('next')}
                    disabled={
                      previewFacilityIndex === facilities.length - 1 && 
                      previewListingIndex === (facilities[previewFacilityIndex]?.listings?.length || 0) - 1
                    }
                  >
                    Next Listing
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      case 3:
        return (
          <ReviewStep 
            group={group}
            facilities={facilities}
            errors={errors}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // Success screen
  if (submitSuccess) {
    return (
      <div className="group-intake-container">
        <div className="intake-main">
          <div className="form-section">
            <div className="form-header">
              <h1 className="page-title">
                <CheckCircle size={24} className="icon" />
                Success!
              </h1>
            </div>
            <div className="form-content text-center">
              <h2 className="mb-4">Your group submission was successful!</h2>
              <p className="mb-4">Our team will review your group information and listings.</p>
              <p className="mb-4">You'll be notified when your listings are live and ready to be seen by CRNAs.</p>
              <div className="action-buttons justify-content-center">
                <Button className="btn-primary" onClick={() => window.location.href = '/'}>
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className="group-intake-container">
        <div className="intake-main">
          <div className="form-section">
            <div className="form-header">
              <h1 className="page-title">
                <Building2 size={24} className="icon" />
                Loading Group Data...
              </h1>
            </div>
            <div className="form-content text-center">
              <p>Please wait while we load your group information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group-intake-container">
      <div className="form-header">
        <h1 className="page-title">
          <Building2 size={24} className="icon" />
          Group Intake
        </h1>
        {/* {lastAutosave && (
          <div className="autosave-indicator">
            <small className={`autosave-status autosave-${autosaveStatus.type}`}>
              {autosaveStatus.message || 'Last saved: ' + lastAutosave.toLocaleTimeString()}
            </small>
          </div>
        )} */}
      </div>
      
      {/* Tabs */}
      <div className="intake-tabs">
        {steps.map((step, index) => (
          <button
            key={step.id}
            className={`intake-tab ${currentStep === index ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            onClick={() => goToStep(index)}
          >
            <span className="step-number">{index + 1}</span>
            {step.icon && <span className="me-1">{step.icon}</span>}
            {step.name}
          </button>
        ))}
      </div>

      <div className="split-view">
        <div className="form-section">
          {renderStepContent()}
        </div>
      </div>
      
      {/* Modals */}
      <ConfirmDeleteModal
        show={showDelFacModal}
        title="Delete Facility"
        message={`Are you sure you want to delete this facility? All listings for this facility will also be deleted.`}
        onCancel={() => setShowDelFacModal(false)}
        onConfirm={() => {
          try {
            // Use handleConfirmDeleteFacility if available
            if (typeof handleConfirmDeleteFacility === 'function') {
              handleConfirmDeleteFacility();
              // The modal closing should be handled inside handleConfirmDeleteFacility
              return;
            }
            
            // Otherwise, handle it here
            if (facToRemove && typeof facToRemove === 'object' && facToRemove.id) {
              // If facToRemove is an object with id and index
              removeFacility(facToRemove.id, facToRemove.index);
            } else if (facToRemove !== null && facToRemove !== undefined) {
              // If facToRemove is just an index (legacy approach)
              const facilityId = facilities[facToRemove]?.id;
              if (facilityId) {
                removeFacility(facilityId, facToRemove);
              }
            }
            
            // Close the modal using the appropriate function
            if (typeof closeDeleteFacilityModal === 'function') {
              closeDeleteFacilityModal();
            } else if (typeof setShowDelFacModal === 'function') {
              setShowDelFacModal(false);
            }
          } catch (err) {
            console.error('Error in facility deletion:', err);
            // Try to close the modal even if there was an error
            try {
              if (typeof closeDeleteFacilityModal === 'function') closeDeleteFacilityModal();
              else if (typeof setShowDelFacModal === 'function') setShowDelFacModal(false);
            } catch {}
          }
        }}
      />
      
      <ConfirmDeleteModal
        show={showDelListModal}
        title="Delete Listing"
        message={`Are you sure you want to delete this listing?`}
        onCancel={() => setShowDelListModal(false)}
        onConfirm={() => {
          // We only need one delete call, not both
          // Using the context version which properly handles the API call
          deleteListingContext(listToRemove.facIdx, listToRemove.listIdx);
          // Close the modal
          setShowDelListModal(false);
        }}
      />
    </div>
  );
}
